import React, { Component, useMemo } from 'react';
import { View, Text, ActivityIndicator, StyleSheet, useWindowDimensions } from 'react-native';

class KpiErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <View style={{ paddingVertical: 20, alignItems: 'center' }}>
          <Text style={{ fontSize: 12, color: '#C62828' }}>KPI dashboard unavailable</Text>
        </View>
      );
    }
    return this.props.children;
  }
}
import { useDashboardKPIData, LOG_TITLES } from './useDashboardKPIData';
import processData from '../../common/service/dataProcessor';
import ComplianceModule    from './modules/ComplianceModule';
import SustainabilityModule from './modules/SustainabilityModule';
import MaterialModule      from './modules/MaterialModule';
import NuisanceModule      from './modules/NuisanceModule';
import WorkforceModule     from './modules/WorkforceModule';
import PackageLeaderboard  from './modules/PackageLeaderboard';

// Logs without a date field — filter by package only, not date.
const DATE_EXEMPT = new Set(['recycled', 'regional', 'hazardous']);

interface Props {
  accessToken:     string;
  clientAPIURL:    string;
  projectId:       string;
  dateRange:       { start: Date | null; end: Date | null };
  selectedPackage: string[] | null;  // null = All Packages
}

function filterRaw(
  items: any[],
  dateRange: Props['dateRange'],
  selectedPackage: string[] | null,
  useDateFilter: boolean,
): any[] {
  return items.filter(item => {
    if (selectedPackage?.length && !selectedPackage.includes(item.packageName)) return false;
    if (!useDateFilter || (!dateRange.start && !dateRange.end)) return true;
    const itemDate = item.Date && new Date(item.Date);
    if (!itemDate || isNaN(itemDate.getTime())) return false;
    if (dateRange.start) {
      const s = new Date(dateRange.start); s.setHours(0, 0, 0, 0);
      if (itemDate < s) return false;
    }
    if (dateRange.end) {
      const e = new Date(dateRange.end); e.setHours(23, 59, 59, 999);
      if (itemDate > e) return false;
    }
    return true;
  });
}

export default function DashboardKPISection({ accessToken, clientAPIURL, projectId, dateRange, selectedPackage }: Props) {
  const { width } = useWindowDimensions();
  const isNarrow = width < 480;
  const { rawData, complianceConfig, kpiConfig, isLoading, error } = useDashboardKPIData(accessToken, clientAPIURL, projectId);

  // Filtered + processData-enriched data for selected package & date range
  const filtered = useMemo(() => {
    if (!rawData) return null;
    const enrich = (key: string) => {
      const raw      = (rawData as any)[key] as any[] ?? [];
      const useDate  = !DATE_EXEMPT.has(key);
      const slice    = filterRaw(raw, dateRange, selectedPackage, useDate);
      return processData(LOG_TITLES[key], slice, complianceConfig) ?? [];
    };
    return {
      observations: enrich('observations'),
      energy:       enrich('energy'),
      water:        enrich('water'),
      waste:        enrich('waste'),
      spoil:        enrich('spoil'),
      importedSoil: enrich('importedSoil'),
      recycled:     enrich('recycled'),
      regional:     enrich('regional'),
      training:     enrich('training'),
      noise:        enrich('noise'),
      vibration:    enrich('vibration'),
      pm10:         enrich('pm10'),
      pm25:         enrich('pm25'),
    };
  }, [rawData, complianceConfig, dateRange, selectedPackage]);

  // Leaderboard uses date filter only (no package filter) so all packages are visible
  const leaderboardData = useMemo(() => {
    if (!rawData) return null;
    const enrich = (key: string) => {
      const raw     = (rawData as any)[key] as any[] ?? [];
      const useDate = !DATE_EXEMPT.has(key);
      const slice   = filterRaw(raw, dateRange, null, useDate);
      return processData(LOG_TITLES[key], slice, complianceConfig) ?? [];
    };
    return {
      observations: enrich('observations'),
      waste:        enrich('waste'),
      training:     enrich('training'),
      noise:        enrich('noise'),
      vibration:    enrich('vibration'),
      pm10:         enrich('pm10'),
      pm25:         enrich('pm25'),
    };
  }, [rawData, complianceConfig, dateRange]);

  if (isLoading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color="#1C60A5" />
        <Text style={styles.loaderText}>Loading KPI dashboard…</Text>
      </View>
    );
  }

  if (error || !filtered || !leaderboardData) {
    return (
      <View style={styles.errorBox}>
        <Text style={styles.errorText}>{error ?? 'KPI data unavailable'}</Text>
      </View>
    );
  }

  const { modules } = kpiConfig;

  if (!modules.enabled) return null;

  return (
    <KpiErrorBoundary>
      <View style={[styles.section, isNarrow && styles.sectionNarrow]}>

        {/* Row 1: Compliance + Sustainability */}
        {(modules.compliance || modules.sustainability) && (
          <View style={styles.moduleRow}>
            {modules.compliance && (
              <View style={styles.moduleCol}>
                <KpiErrorBoundary>
                  <ComplianceModule
                    observations={filtered.observations}
                    config={kpiConfig}
                    stretch
                  />
                </KpiErrorBoundary>
              </View>
            )}
            {modules.sustainability && (
              <View style={styles.moduleCol}>
                <KpiErrorBoundary>
                  <SustainabilityModule
                    energy={filtered.energy}
                    water={filtered.water}
                    waste={filtered.waste}
                    selectedPackage={selectedPackage}
                    config={kpiConfig}
                    stretch
                  />
                </KpiErrorBoundary>
              </View>
            )}
          </View>
        )}

        {/* Row 2: Material Circularity + Community Nuisance */}
        {(modules.materials || modules.nuisance) && (
          <View style={styles.moduleRow}>
            {modules.materials && (
              <View style={styles.moduleCol}>
                <KpiErrorBoundary>
                  <MaterialModule
                    spoil={filtered.spoil}
                    importedSoil={filtered.importedSoil}
                    recycled={filtered.recycled}
                    regional={filtered.regional}
                    stretch
                  />
                </KpiErrorBoundary>
              </View>
            )}
            {modules.nuisance && (
              <View style={styles.moduleCol}>
                <KpiErrorBoundary>
                  <NuisanceModule
                    noise={filtered.noise}
                    vibration={filtered.vibration}
                    pm10={filtered.pm10}
                    pm25={filtered.pm25}
                    stretch
                  />
                </KpiErrorBoundary>
              </View>
            )}
          </View>
        )}

        {/* Row 3: Workforce (full width) */}
        {modules.workforce && (
          <KpiErrorBoundary>
            <WorkforceModule
              training={filtered.training}
              selectedPackage={selectedPackage}
              config={kpiConfig}
            />
          </KpiErrorBoundary>
        )}

        {/* Row 4: Package Leaderboard (full width) */}
        {modules.packageLeaderboard && (
          <KpiErrorBoundary>
            <PackageLeaderboard
              allData={leaderboardData}
              config={kpiConfig}
            />
          </KpiErrorBoundary>
        )}

      </View>
    </KpiErrorBoundary>
  );
}

const styles = StyleSheet.create({
  moduleRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'stretch',
  },
  moduleCol: {
    flex: 1,
    minWidth: 400,
    flexDirection: 'column',
  },
  section: {
    marginBottom: 24,
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  sectionNarrow: {
    marginBottom: 16,
  },
  sectionHeading: {
    fontSize: 16,
    fontWeight: '800',
    color: '#1C60A5',
    marginBottom: 14,
    paddingHorizontal: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  sectionHeadingNarrow: {
    fontSize: 13,
    marginBottom: 10,
    letterSpacing: 0.4,
  },
  loader: {
    paddingVertical: 40,
    alignItems: 'center',
    gap: 10,
  },
  loaderText: {
    fontSize: 13,
    color: '#999',
  },
  errorBox: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  errorText: {
    fontSize: 12,
    color: '#C62828',
  },
});
